import { get, set } from 'lib/versionedLocalStorage'
import { get as getSS, set as setSS } from 'lib/isomorphicSessionStorage'

const STORAGE_KEY = 'tripReengagementCardDismissal'
const STORAGE_VERSION = 1

const SESSION_LOG_KEY = 'tripReengagementSessionLogged'

interface TripReengagementDismissalState {
  isDismissed: boolean;
  dismissedAt: number;
  sessionsSinceDismissal: number;
  totalClosures: number;
}

const initialState: TripReengagementDismissalState = {
  isDismissed: false,
  dismissedAt: 0,
  sessionsSinceDismissal: 0,
  totalClosures: 0,
}

export function setTripReengagementDismissedInStorage(type: 'closed' | 'clicked') {
  const state = getTripReengagementDismissalState()
  // Don't count clicks toward total closures
  const totalClosures = type === 'closed' ? state.totalClosures + 1 : state.totalClosures
  setTripReengagementDismissalState({
    isDismissed: true,
    dismissedAt: Date.now(),
    sessionsSinceDismissal: 0,
    totalClosures,
  })
  setSS(SESSION_LOG_KEY, true)
}

const threeDaysMilliseconds = 259200000

/**
 * If the trip reengagement card has been dismissed for at least 3 days and
 * the user has had 3 sessions since then, the dismissal state is reset so
 * that the card can be shown again. If they've dismissed it 3 times, it
 * will never be shown again.
 */
export function getDismissalStateAndResetIfExpired() {
  const state = getTripReengagementDismissalState()
  if (
    state.isDismissed &&
    state.sessionsSinceDismissal >= 3 &&
    Date.now() - state.dismissedAt > threeDaysMilliseconds &&
    state.totalClosures < 3
  ) {
    const newState = {
      ...state,
      isDismissed: false,
      dismissedAt: 0,
    }
    setTripReengagementDismissalState(newState)
    return newState
  }
  return state
}

/**
 * This function uses sessionstorage to track whether a session has been logged.
 * This is used to count the number of sessions since the card was dismissed.
 */
export function logSessionSinceTripReengagementDismissal() {
  const sessionLogged = getSS(SESSION_LOG_KEY)
  if (!sessionLogged) {
    setSS(SESSION_LOG_KEY, true)
    const state = getTripReengagementDismissalState()
    if (state.isDismissed) {
      setTripReengagementDismissalState({
        ...state,
        sessionsSinceDismissal: state.sessionsSinceDismissal + 1,
      })
    }
  }
}

function setTripReengagementDismissalState(state: TripReengagementDismissalState) {
  return set(STORAGE_KEY, STORAGE_VERSION, state)
}

function getTripReengagementDismissalState(): TripReengagementDismissalState {
  return get(STORAGE_KEY, STORAGE_VERSION) ?? initialState
}
